import { TextField, TextFieldProps, styled, Box } from "@mui/material";
import { tokens } from "../../theme/tokens";

interface TrackNowTextareaProps extends Omit<TextFieldProps, 'variant' | 'multiline'> {
  success?: boolean;
  successMessage?: string;
  minRows?: number;
}

const StyledTextarea = styled(TextField)<TrackNowTextareaProps>(({ success }) => ({
  '& .MuiOutlinedInput-root': {
    minHeight: '120px',
    borderRadius: '10px',
    fontFamily: 'Area Normal, Inter, sans-serif',
    fontSize: '16px',
    fontWeight: 400,
    backgroundColor: tokens.colors.white,
    
    '& fieldset': {
      borderColor: tokens.colors.midGrey,
      borderWidth: '1px',
    },
    
    '&:hover:not(.Mui-disabled) fieldset': {
      borderColor: tokens.colors.modernBlue,
      borderWidth: '1px',
    },
    
    '&.Mui-focused fieldset': {
      borderColor: `${tokens.colors.connectedPurple} !important`,
      borderWidth: '2px !important',
    },
    
    '&.Mui-error fieldset': {
      borderColor: tokens.colors.midGrey,
      borderWidth: '1px',
    },
    
    '&.Mui-disabled': {
      backgroundColor: tokens.colors.offWhite,
      '& fieldset': {
        borderColor: tokens.colors.offWhite,
      },
      '& textarea': {
        color: tokens.colors.grey,
        cursor: 'not-allowed',
        WebkitTextFillColor: tokens.colors.grey,
      },
    },
    
    '& textarea': {
      padding: '12px 14px',
      color: tokens.colors.offBlack,
      resize: 'vertical',
      '&::placeholder': {
        color: tokens.colors.grey,
        opacity: 1,
      },
    },
    
    // Purple text while typing (focused)
    '&.Mui-focused textarea': {
      color: tokens.colors.connectedPurple,
    },
  },
  
  // Remove all label styling - labels will be external
  '& .MuiInputLabel-root': {
    display: 'none',
  },
  
  '& .MuiFormHelperText-root': {
    fontFamily: 'Area Normal, Inter, sans-serif',
    fontSize: '12px',
    fontWeight: 400,
    textTransform: 'none',
    marginLeft: 0,
    marginTop: '6px',
    
    '&.Mui-error': {
      color: tokens.colors.error,
    },
  },
  
  ...(success && {
    '& .MuiOutlinedInput-root fieldset': {
      borderColor: tokens.colors.midGrey,
      borderWidth: '1px',
    },
    '& .MuiFormHelperText-root': {
      color: tokens.colors.success,
    },
  }),
}));

const StyledLabel = styled(Box)(() => ({
  fontFamily: 'Area Normal, Inter, sans-serif',
  fontSize: '14px',
  fontWeight: 700,
  letterSpacing: '0.08em',
  textTransform: 'uppercase',
  color: tokens.colors.grey,
  marginBottom: '6px',
  display: 'block',
}));

export const TrackNowTextarea = ({ 
  success, 
  successMessage, 
  helperText, 
  label, 
  minRows = 4,
  ...props 
}: TrackNowTextareaProps) => {
  const displayHelperText = success ? successMessage : helperText;
  
  return (
    <Box>
      {label && <StyledLabel>{label}</StyledLabel>}
      <StyledTextarea
        {...props}
        success={success}
        helperText={displayHelperText}
        variant="outlined"
        multiline
        minRows={minRows}
        label={undefined}
      />
    </Box>
  );
};